import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from 'src/components/common/Header';
import ROUTES from 'src/constants/routes';
import { useTranslation } from 'react-i18next';
import ErrorModal from 'src/components/common/ErrorModal';
import { WorkOrder } from '@twinsketch/topika-model';
import ApiService from 'src/services/ApiService';
import ListHeader from 'src/components/listPage/ListHeader';
import { FaFlag, FaClipboardList } from 'react-icons/fa';
import { useAppData } from 'src/components/AppDataContext';
import SortButton, { CombinedSortableFields } from 'src/components/common/SortButton';
import SearchBar from 'src/components/listPage/SearchBar';
import Pagination from 'src/components/Pagination';
import { useUser } from 'src/auth/UserContext';

const WorkOrderList: React.FC = () => {
  const { workOrders, setWorkOrders } = useAppData();
  const { t } = useTranslation();
  const [urlParams, setUrlParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(urlParams.get('q') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<CombinedSortableFields>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [nameSort, setNameSort] = useState<'asc' | 'desc' | null>(null);
  const [loading, setLoading] = useState(false);

  const pageSize = 20;
  const currentPage = parseInt(urlParams.get('p') || '1');
  const [totalPages, setTotalPages] = useState(Math.ceil(workOrders.length / pageSize));
  const { platformUser: user } = useUser();
  const canWrite = user?.permissions?.includes('write');

  const fileCategories = [
    { key: 'WORK_ORDER', label: t('작업지시서') },
    { key: 'MIXING_REPORT', label: t('믹싱일보') },
    { key: 'COATING_REPORT', label: t('코팅일보') },
    { key: 'PRESS_REPORT', label: t('프레스') },
    { key: 'SLITTING_REPORT', label: t('슬리팅') },
    { key: 'COA', label: 'COA' },
  ];

  const filteredWorkOrders = useMemo(() => {
    return workOrders.filter(workOrder =>
      Object.values(workOrder)
        .filter(field => typeof field === 'string')
        .some(field => field.toLowerCase().includes(searchTerm))
    );
  }, [workOrders, searchTerm]);

  const handleWorkOrderNameSort = () => {
    const nextSort = nameSort === 'asc' ? 'desc' : nameSort === 'desc' ? null : 'asc';
    const nextSortField = nextSort ? 'name' : 'createdAt';
    const nextSortDirection = nextSort || 'asc';

    setNameSort(nextSort);
    setSortBy(nextSortField);
    setSortDirection(nextSortDirection);

    urlParams.set('p', '1');
    urlParams.set('sortField', nextSortField);
    urlParams.set('sortDirection', nextSortDirection);
    setUrlParams(urlParams);

    handleSortChange(nextSortField, nextSortDirection);
  };

  const handleSortChange = async (
    sortField: CombinedSortableFields,
    sortDirection: 'asc' | 'desc'
  ) => {
    setLoading(true);
    try {
      const response = await ApiService.fetchWorkOrders(
        sortField,
        sortDirection,
        currentPage,
        pageSize
      );
      if ('data' in response && 'pagination' in response) {
        setWorkOrders(response.data);
        setTotalPages(response.pagination.totalPages);
      } else {
        setWorkOrders(response);
        setTotalPages(Math.ceil(response.length / pageSize));
      }
    } catch (err) {
      console.error('Error fetching sorted work orders:', err);
      setError(t('Failed to fetch work orders'));
    }
    setLoading(false);
  };

  const formulationsToDisplay = useMemo(() => {
    const formulations = filteredWorkOrders;
    setTotalPages(Math.ceil(formulations.length / pageSize));
    return formulations.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredWorkOrders, currentPage]);

  const renderTableHeaders = () => (
    <tr>
      <th className="flag-column">{t('!')}</th>
      <th onClick={handleWorkOrderNameSort} style={{ cursor: 'pointer' }}>
        {t('workOrderDataField.name')}
        {nameSort === 'asc' ? ' ▲' : nameSort === 'desc' ? ' ▼' : ' •'}
      </th>
      <th>{t('workOrderDataField.formulationName')}</th>
      <th>{t('workOrderDataField.activeMaterialKg')}</th>
      <th>{t('workOrderDataField.mixingTankVolumeFull')}</th>
      <th>{t('Work Order')}</th>
      <th>{t('Mixing')}</th>
      <th>{t('Coating')}</th>
      <th>{t('Press')}</th>
      <th>{t('Slitting')}</th>
      <th>{t('COA')}</th>
      <th>{t('Created By')}</th>
    </tr>
  );

  const renderTableDataCells = (workOrder: WorkOrder) => {
    const getFileCountByCategory = (category: string) =>
      workOrder.attachedFilesInfo?.filter(file => file.category === category).length || 0;

    const getSummaryTabLink = (category: string): string | null => {
      const baseUrl = ROUTES.WORK_ORDERS_DETAIL(workOrder.id);
      switch (category) {
        case 'MIXING_REPORT':
          return `${baseUrl}/mixingWork`;
        case 'COATING_REPORT':
          return `${baseUrl}/coatingWork`;
        case 'PRESS_REPORT':
          return `${baseUrl}/pressWork`;
        case 'SLITTING_REPORT':
          return `${baseUrl}/slitWork`;
        case 'COA':
          return `${baseUrl}/coa`;
        default:
          return null;
      }
    };

    return (
      <>
        <td className="flag-column">
          {workOrder.flagged ? <FaFlag style={{ color: 'red' }} /> : ''}
        </td>
        <td>
          <Link to={ROUTES.WORK_ORDERS_DETAIL(workOrder.id)}>{workOrder.name || '-'}</Link>
        </td>
        <td>{workOrder.formulationName || '-'}</td>
        <td>{workOrder.activeMaterialKg || '-'}</td>
        <td>{workOrder.mixingTankVolumeFull || '-'}</td>
        {fileCategories.map(({ key }) => {
          const count = getFileCountByCategory(key);
          const link = getSummaryTabLink(key);
          const hasData =
            (key === 'MIXING_REPORT' && workOrder.mixingWorks?.length) ||
            (key === 'COATING_REPORT' && workOrder.coatingWorks?.length) ||
            (key === 'PRESS_REPORT' && workOrder.pressWorks?.length) ||
            (key === 'SLITTING_REPORT' && workOrder.slitWorks?.length) ||
            (key === 'COA' && workOrder.coas?.length);

          const isClickable = !!link && !!hasData;
          const iconStyle = {
            color: isClickable ? '#007bff' : 'gray',
            cursor: isClickable ? 'pointer' : 'default',
          };

          return (
            <td key={key}>
              {count > 0 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isClickable ? (
                    <Link to={link} title={t('View Summary')}>
                      <FaClipboardList style={iconStyle} />
                    </Link>
                  ) : (
                    <FaClipboardList style={iconStyle} />
                  )}
                  <span>{count}</span>
                </span>
              )}
            </td>
          );
        })}
        <td>{workOrder.createdBy || '-'}</td>
      </>
    );
  };

  return (
    <div>
      <Header />
      <ErrorModal message={error} onClose={() => setError(null)} />
      <main className="list-main-content">
        <ListHeader
          createRoute={ROUTES.WORK_ORDERS_CREATE}
          addButtonLabel="Add New"
          isAddNewAllowed={canWrite}
        />
        <SearchBar
          placeholder="ex) pvdf"
          value={searchTerm}
          onChange={e => {
            const term = e.target.value.toLowerCase();
            setSearchTerm(term);
            urlParams.set('q', term);
            urlParams.set('p', '1');
            setUrlParams(urlParams);
          }}
        />
        <SortButton
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onSortChange={handleSortChange}
        />
        <div>
          <table>
            <thead>{renderTableHeaders()}</thead>
            <tbody>
              {formulationsToDisplay.map(workOrder => (
                <tr key={workOrder.id}>{renderTableDataCells(workOrder)}</tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={pageNum => {
              urlParams.set('p', `${pageNum}`);
              setUrlParams(urlParams);
            }}
          />
        </div>
      </main>
    </div>
  );
};

export default WorkOrderList;
